import { JsonObject, JsonProperty } from 'json2typescript';

import { DocumentInterface, DocumentStatus, DocumentTypeId } from '../../../core';
import { DocumentStatusAdapter } from '../../client/helpers/serializer/adapters/document-status.adapter';
import { Id } from './id';
import { IdAdapter } from '../../client/helpers/serializer/adapters/id.adapter';
import { NumberToBooleanAdapter } from '../../client/helpers/serializer/adapters/number-to-boolean.adapter';
import { StringAdapter } from '../../client/helpers/serializer/adapters/string.adapter';

@JsonObject('Document')
export class Document implements DocumentInterface {
    @JsonProperty('DocumentID', IdAdapter, true)
    private _id: Id = undefined;

    @JsonProperty('TypeId', IdAdapter, true)
    private _typeId: Id = undefined;

    @JsonProperty('Name', StringAdapter, true)
    private _name: string = undefined;

    @JsonProperty('ClientName', StringAdapter, true)
    private _clientName: string = undefined;

    private _file: File = undefined;

    @JsonProperty('DocumentFilename', StringAdapter, true)
    private _filename: string = undefined;

    @JsonProperty('DocumentContent', StringAdapter, true)
    private _content: string = undefined;

    @JsonProperty('DocumentSize', StringAdapter, true)
    private _size: string = undefined;

    @JsonProperty('DocumentType', StringAdapter, true)
    private _type: string = undefined;

    @JsonProperty('DocumentUrl', StringAdapter, true)
    private _url: string = undefined;

    @JsonProperty('Optional', NumberToBooleanAdapter, true)
    private _optional: boolean = undefined;

    @JsonProperty('Allowdelete', Boolean, true)
    private _allowRemove: boolean = undefined;

    private _remove: boolean = undefined;

    @JsonProperty('Validate', DocumentStatusAdapter, true)
    private _status: DocumentStatus = undefined;

    constructor(typeId?: Id) {
        this._typeId = typeId;
        this._name = '';
        this._optional = false;
        this._allowRemove = true;
        this._remove = false;
    }

    get id(): Id {
        return this._id;
    }

    get typeId(): Id {
        return this._typeId;
    }

    get isIdentificationType(): boolean {
        return (
            [
                DocumentTypeId.DNI,
                DocumentTypeId.NIF,
                DocumentTypeId.CIF,
                DocumentTypeId.NIE,
                DocumentTypeId.NIE_NOT_RESIDENT,
                DocumentTypeId.IDENTIFICATION_CARD_NOT_RESIDENT,
                DocumentTypeId.IDENTIFICATION_DOCUMENT,
                DocumentTypeId.PASSPORT,
                DocumentTypeId.PASSPORT_LATIN,
                DocumentTypeId.PERMISSION,

            ].indexOf(String(this.typeId) as any) !== -1
        );
    }

    get isGuardianShip(): boolean {
        return this._name.indexOf('tutela') !== -1;
    }

    get name(): string {
        const name = this._name + (!this._optional ? '*' : '');

        if (this.isGuardianShip) {
            return name + ' ' + this._clientName;
        }

        return name;
    }

    get originalName(): string {
        return this._name?.replace('(opcional)', '').trim();
    }

    get file(): File {
        return this._file;
    }

    set file(value: File) {
        this._file = value;

        if (this.id && value) {
            this.remove = true;
        } else if (this.id && !value) {
            this.remove = false;
        }
    }

    get filename(): string {
        return this._filename;
    }

    get content(): string {
        return this._content;
    }

    get size(): string {
        return this._size;
    }

    get type(): string {
        return this._type;
    }

    get url(): string {
        return this._url;
    }

    get optional(): boolean {
        return this._optional;
    }

    get allowRemove(): boolean {
        return this._allowRemove;
    }

    get remove(): boolean {
        return this._remove;
    }

    set remove(value: boolean) {
        this._remove = value;
    }

    get status(): DocumentStatus {
        return this._status;
    }

    get allowUploadMultiple(): boolean {
        return (
            String(this.typeId) === DocumentTypeId.DNI ||
            String(this.typeId) === DocumentTypeId.NIE ||
            String(this.typeId) === DocumentTypeId.NIE_NOT_RESIDENT
        );
    }

    get iconPath(): string {
        switch (String(this.typeId)) {
            case DocumentTypeId.DNI:
            case DocumentTypeId.NIE:
            case DocumentTypeId.NIE_NOT_RESIDENT:
                return 'assets/images/credit-card-off.svg';
            case DocumentTypeId.DNI_FRONT_SIDE:
            case DocumentTypeId.NIE_FRONT_SIDE:
            case DocumentTypeId.NIE_NOT_RESIDENT_FRONT_SIDE:
                return 'assets/images/id-card.svg';
            case DocumentTypeId.DNI_BACK_SIDE:
            case DocumentTypeId.NIE_BACK_SIDE:
            case DocumentTypeId.NIE_NOT_RESIDENT_BACK_SIDE:
                return 'assets/images/credit-card-blank.svg';
            default:
                return undefined;
        }
    }

    isValidated(): boolean {
        return this.status === DocumentStatus.VALIDATE;
    }

    isComplete(): boolean {
        if (this.optional) {
            return true;
        }

        return (!!this.id || !!this.file) && (!this.remove || (this.remove && !!this.file));
    }
}
